import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { Navigate, useLocation } from "react-router-dom";

import { VirtualizedItemList } from "@/components/item-list";
import { ItemListRowFeaturedContent } from "@/components/item-list/rows/components/item-list-row-featured-content";
import { appRoutes } from "@/app/router/appRoutes";
import { ItemListItemKind, ItemListRowData, ItemListRowType } from "@/components/item-list/types";
import type { ItemListCarouselRowData } from "@/components/item-list/types/item-list-row-types";
import { getItemListCarouselItemForCollection } from "@/components/item-list/helpers/get-item-list-carousel-item-for-collection";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { ClearFiltersModal, useClearFiltersModal } from "@/components/filters/clear-filters-modal";
import { CollectionsFacetFilter } from "@/components/filters/collections-facet-filter";
import { ContactsFacetFilter } from "@/components/filters/contacts-facet-filter";
import { DateFacetFilter } from "@/components/filters/date-facet-filter";
import { MediaKindsFacetFilter } from "@/components/filters/media-kinds-facet-filter";
import { generateShortDateString } from "@/domains/date/date";
import type { Uuid } from "@/domains/global/identifiers";
import { itemsModule } from "@/modules/items";
import { SearchPageFilter } from "@/modules/filters/search/SearchPageFilter";
import { SortByKind } from "@/modules/lenses/types";
import type { ValidSearchSortByValue } from "@/store/pages/SearchPageStore/types";
import {
  MDSFadeHorizontalScroll,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSIconButton,
  MDSIconButtonSize,
  MDSPanelContainer,
  MDSPanelHeader,
  MDSPanelHeaderButtonGroup,
  MDSPanelHeaderSlotName,
  MDSProgressBar,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import { ItemPreviewState } from "@/components/item-preview/item-preview-state";
import { ImageLightbox } from "@/components/note/media-viewer/image/image-lightbox";
import { PdfLightbox } from "@/components/note/media-viewer/pdf/pdf-lightbox";
import { useAppStore } from "@/store";
import type { AgentSearchAttachmentPreview } from "@/store/pages/AgentSearchPageStore/AgentSearchPageStore";
import { ApiSchema } from "@/modules/api";
import { noop } from "lodash-es";
import { SortByDropdownButton } from "@/components/filters/sort-by-dropdown-button";
import { SearchResultPdfChips } from "@/pages/search/components/search-result-pdf-chips";
import { SearchPageNotesListEmpty } from "@/pages/search/components/search-page-notes-list-empty";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import type { SearchResultFeaturedSnippetRows } from "@/store/pages/SearchPageStore/resolveSearchResultFeaturedSnippet";

export const AgentSearchPage = observer(function AgentSearchPage() {
  const location = useLocation();
  const isAgenticSearchEnabled = false;

  if (!isAgenticSearchEnabled) {
    return (
      <Navigate
        replace
        to={{
          pathname: appRoutes.search({}).path,
          search: location.search,
        }}
      />
    );
  }

  return <AgentSearchPageContent />;
});

const AgentSearchPageContent = observer(function AgentSearchPageContent() {
  const { store } = useAppStore();
  const page = useTabPageStore("agentSearchPage");
  page.listState.useListStateEffects();
  const {
    filterButtonRef,
    showFilters,
    showFiltersModal,
    setShowFiltersModal,
    handleToggleFilters,
    handleClearFilters,
  } = useClearFiltersModal({
    clearAllFilters: page.filters.clearAllFilters,
    activeFiltersCount: page.filters.activeFiltersCount,
    initialShowFilters: true,
  });

  const itemRows: ItemListRowData[] = [];
  let carouselPosition = 0;

  for (const row of page.rows) {
    if (row.kind === "heading") {
      itemRows.push({
        type: ItemListRowType.SectionHeader,
        key: `heading-${page.getHeadingLabel(row.result)}-${itemRows.length}`,
        payload: {
          title: page.getHeadingLabel(row.result),
        },
      });
      continue;
    }

    if (row.kind === "carousel") {
      const value = row.result.value as ApiSchema["SearchEngineResultSectionCarouselValue"];
      const carousel: ItemListCarouselRowData = {
        type: ItemListRowType.Carousel,
        key: `carousel-${itemRows.length}`,
        payload: {
          items: [],
        },
      };
      const sortBy = (page.filters.sortBy ?? SortByKind.Relevance) as ValidSearchSortByValue;

      for (const item of value.items ?? []) {
        if (item.kind !== "COLLECTION") continue;

        const { collection_id: collectionId } =
          item.value as ApiSchema["SearchEngineResultSectionCarouselItemCollectionValue"];
        const collection = store.collections.getCollectionObservableById({
          collectionId: collectionId as Uuid,
        });
        if (!collection) continue;

        carousel.payload.items.push(
          getItemListCarouselItemForCollection({
            collection,
            store,
            position: carouselPosition,
            query: page.query,
            sortBy,
          })
        );
        carouselPosition += 1;
      }

      if (carousel.payload.items.length > 0) {
        itemRows.push(carousel);
      }
      continue;
    }

    if (row.kind === "group") {
      itemRows.push({
        type: ItemListRowType.SectionHeader,
        key: `group-${row.group.id}`,
        payload: {
          title: row.group.label,
          titleIcon: {
            kind: MDSIconKind.Radar,
            color: MDSIconColor.Emphasis,
            tooltip: "Agent-generated group",
          },
        },
      });
      continue;
    }

    if (row.kind === "otherResultsHeader") {
      itemRows.push({
        type: ItemListRowType.SectionHeader,
        key: "other-results",
        payload: {
          title: "Other results",
        },
      });
      continue;
    }

    const resultId = page.getResultId(row.result);
    if (!resultId) continue;

    const noteId = page.getResultNoteId(row.result);
    const collectionId = page.getResultCollectionId(row.result);
    const isHighlighted = page.highlightedResultIds.has(resultId);
    const isAppended = page.appendedResultIds.has(resultId);
    const isAgentResult = isHighlighted || isAppended;
    const hint = page.getResultHint(resultId);
    const preview = page.getResultPreview(row.result);
    const snippetRows = noteId ? page.getResultSnippetRows(row.result, preview) : [];
    const titleIcon = isAgentResult
      ? {
          kind: MDSIconKind.Radar,
          color: MDSIconColor.Emphasis,
          tooltip: "Deep Search result",
        }
      : undefined;
    const extraRow = buildAgentSearchExtraRow({
      resultId,
      preview: snippetRows.length ? "" : preview,
      hint,
      snippetRows,
    });

    if (noteId) {
      const note = store.notes.get(noteId as Uuid);
      if (!note) {
        const itemAppearance = itemsModule.resolveAppearance({
          itemId: noteId,
          appStore: store,
        });

        itemRows.push({
          type: ItemListRowType.Item,
          key: resultId,
          payload: {
            id: noteId as Uuid,
            kind: ItemListItemKind.Note,
            appearance: itemAppearance,
            label: page.getResultTitle(row.result),
            onClick: ({ event }) => {
              store.navigation.goToNote({
                noteId: noteId as Uuid,
                event,
                activeTabId: page.tabId,
              });
            },
            titleIcon,
            isSpecial: isAgentResult,
            extraRow,
            listState: page.listState,
          },
        });
        continue;
      }

      itemRows.push({
        type: ItemListRowType.AsyncNote,
        key: resultId,
        payload: {
          noteId,
          itemRow: note => {
            const itemAppearance = itemsModule.resolveAppearance({
              itemId: note.id,
              appStore: store,
            });
            const attachmentPreview = page.getAttachmentPreview(row.result, note);
            const collections = note.collectionList?.allCollections || [];
            const secondaryLabel = note.secondaryTitle || "No additional text";
            const noteSnippetRows = page.getResultSnippetRows(row.result, secondaryLabel);
            const noteExtraRow = buildAgentSearchExtraRow({
              resultId,
              preview: noteSnippetRows.length ? "" : preview,
              hint,
              attachmentPreview,
              snippetRows: noteSnippetRows,
            });

            return {
              type: ItemListRowType.Item,
              key: resultId,
              payload: {
                id: note.id,
                kind: ItemListItemKind.Note,
                appearance: itemAppearance,
                createPreviewState: () =>
                  new ItemPreviewState({
                    store,
                    id: note.id,
                    kind: ItemListItemKind.Note,
                  }),
                label: note.title,
                description: note.secondaryTitle || "No additional text",
                onClick: ({ event }) => {
                  store.navigation.goToNote({
                    noteId: note.id,
                    event,
                    activeTabId: page.tabId,
                  });
                },
                sharedBy: note.sharedBy,
                titleIcon,
                dateLabel: generateShortDateString(note.modifiedAt),
                isSpecial: isAgentResult,
                chips: () => {
                  const sortedCollections = sortCollectionsForChips(collections);
                  if (sortedCollections.length === 0) return null;
                  return <ItemListRowFeaturedContent inline collections={sortedCollections} />;
                },
                extraRow: noteExtraRow,
                listState: page.listState,
              },
            };
          },
        },
      });
      continue;
    }

    if (collectionId) {
      const collection = store.collections.get(collectionId as Uuid);
      if (!collection) {
        const itemAppearance = itemsModule.resolveAppearance({
          itemId: collectionId,
          appStore: store,
        });

        itemRows.push({
          type: ItemListRowType.Item,
          key: resultId,
          payload: {
            id: collectionId as Uuid,
            kind: ItemListItemKind.Collection,
            appearance: itemAppearance,
            label: page.getResultTitle(row.result),
            onClick: ({ event }) => {
              store.navigation.goToCollection({
                collectionId: collectionId as Uuid,
                event,
                activeTabId: page.tabId,
              });
            },
            titleIcon,
            isSpecial: isAgentResult,
            extraRow,
            listState: page.listState,
          },
        });
        continue;
      }

      itemRows.push({
        type: ItemListRowType.AsyncCollection,
        key: resultId,
        payload: {
          collectionId,
          itemRow: collection => {
            const itemAppearance = itemsModule.resolveAppearance({
              itemId: collection.id,
              appStore: store,
            });

            return {
              type: ItemListRowType.Item,
              key: resultId,
              payload: {
                id: collection.id as Uuid,
                kind: ItemListItemKind.Collection,
                appearance: itemAppearance,
                createPreviewState: () =>
                  new ItemPreviewState({
                    store,
                    id: collection.id,
                    kind: ItemListItemKind.Collection,
                  }),
                label: collection.label,
                onClick: ({ event }) => {
                  store.navigation.goToCollection({
                    collectionId: collection.id,
                    event,
                    activeTabId: page.tabId,
                  });
                },
                sharedBy: collection.sharedBy,
                titleIcon,
                dateLabel: generateShortDateString(collection.modifiedAt),
                isSpecial: isAgentResult,
                extraRow,
                listState: page.listState,
              },
            };
          },
        },
      });
    }
  }

  if (!page.isLoading && !page.isAgentRunning && itemRows.length === 0) {
    itemRows.push({
      type: ItemListRowType.Other,
      key: "empty",
      payload: {
        children: (
          <SearchPageNotesListEmpty
            query={page.query}
            isSuccess={!page.errorMessage}
            anyFiltersSelected={page.filters.hasAnyFilters}
            onRetryClick={page.search}
            onClearFiltersClick={page.filters.clearAllFilters}
          />
        ),
      },
    });
  }

  return (
    <Wrapper data-test-id="agent-search-page">
      <MDSPanelHeader>
        <StyledHeading slot={MDSPanelHeaderSlotName.Heading}>
          <MDSIcon kind={MDSIconKind.Search} size={18} />
          <MDSText variant={MDSTextVariant.HeaderXl} numberOfLines={1}>
            {page.query ? page.query : "Agentic search"}
          </MDSText>
        </StyledHeading>
        <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
          <MDSIconButton
            ref={filterButtonRef}
            size={MDSIconButtonSize.Medium}
            iconKind={MDSIconKind.Filter}
            onClick={handleToggleFilters}
            isToggled={showFilters}
          />
        </MDSPanelHeaderButtonGroup>
      </MDSPanelHeader>
      {(page.isLoading || page.isAgentRunning) && (
        <MDSProgressBar aria-label="Agentic search progress" isIndeterminate />
      )}
      {showFilters && (
        <FiltersWrapper>
          <FiltersContainer>
            <SortByDropdownButton
              sortOptions={page.filters.sortOptions}
              defaultSortOption={SortByKind.Relevance}
              selectedSortOption={page.filters.sortBy}
              onSelectSortOption={page.filters.setSortBy}
              alwaysToggled
            />
            {renderSortedFilters(page.filters)}
          </FiltersContainer>
        </FiltersWrapper>
      )}
      <PanelContainer>
        {(page.errorMessage || page.statusMessage) && (
          <FloatingStatusPill $isError={Boolean(page.errorMessage)}>
            <MDSIcon
              kind={MDSIconKind.Radar}
              size={14}
              boxSize={16}
              color={page.errorMessage ? MDSIconColor.Danger : MDSIconColor.OnAccent}
            />
            <StatusMessageText
              variant={MDSTextVariant.TextS}
              color={page.errorMessage ? MDSTextColor.Danger : MDSTextColor.OnAccent}
              $isError={Boolean(page.errorMessage)}
            >
              {page.errorMessage || page.statusMessage || "Ready"}
            </StatusMessageText>
          </FloatingStatusPill>
        )}
        <VirtualizedItemList
          rows={itemRows}
          listState={page.listState}
          loading={page.isLoading}
          endReached={() => void page.loadNextFastPage()}
        />
      </PanelContainer>
      <ClearFiltersModal
        triggerRef={filterButtonRef}
        isOpen={showFiltersModal}
        count={page.filters.activeFiltersCount}
        onCancel={() => setShowFiltersModal(false)}
        onConfirm={handleClearFilters}
      />
      <PdfLightbox
        isOpen={!!page.pdfLightboxState}
        src={page.pdfLightboxState?.src ?? ""}
        fileName={page.pdfLightboxState?.fileName}
        initialPage={page.pdfLightboxState?.initialPage}
        context="search_result"
        handleClose={page.closePdfLightbox}
        onOpenChange={open => {
          if (!open) {
            page.closePdfLightbox();
          }
        }}
      />
      <ImageLightbox
        isOpen={!!page.imageLightboxState}
        images={page.imageLightboxState?.images}
        initialIndex={page.imageLightboxState?.initialIndex}
        context="search_result"
        handleClose={page.closeImageLightbox}
        onOpenChange={open => {
          if (!open) {
            page.closeImageLightbox();
          }
        }}
      />
    </Wrapper>
  );
});

const buildAgentSearchExtraRow = ({
  resultId,
  preview,
  hint,
  attachmentPreview,
  snippetRows = [],
}: {
  resultId: string;
  preview: string;
  hint: string | null;
  attachmentPreview?: AgentSearchAttachmentPreview | null;
  snippetRows?: SearchResultFeaturedSnippetRows;
}) => {
  const hasAttachmentPreview =
    !!attachmentPreview &&
    (attachmentPreview.pdfChips.length > 0 || attachmentPreview.imageThumbnails.length > 0);

  if (!hint && !preview && !hasAttachmentPreview && snippetRows.length === 0) return undefined;

  return {
    id: `agent-search-extra-${resultId}`,
    content: () => (
      <ExtraRowContent>
        {snippetRows.map((snippetRow, index) => (
          <ItemListRowFeaturedContent
            key={`agent-search-${resultId}-snippet-row-${index}`}
            snippet={snippetRow}
          />
        ))}
        {preview && (
          <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Secondary}>
            {preview}
          </MDSText>
        )}
        {hasAttachmentPreview && (
          <SearchResultPdfChips
            chips={attachmentPreview.pdfChips}
            imageThumbnails={attachmentPreview.imageThumbnails}
            mediaOrder={attachmentPreview.mediaOrder}
          />
        )}
        {hint && (
          <HintText variant={MDSTextVariant.TextS} color={MDSTextColor.Link}>
            {hint}
          </HintText>
        )}
      </ExtraRowContent>
    ),
  };
};

const renderSortedFilters = (pageFilters: SearchPageFilter) => {
  const filters = [
    {
      isActive: pageFilters.collectionsFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 0,
      component: (
        <CollectionsFacetFilter
          key="collections"
          searchText={pageFilters.collectionsSearchQuery}
          items={pageFilters.collectionsFacetFilterItems}
          onSearchTextChange={pageFilters.setCollectionSearchQuery}
          onSearchTextClear={pageFilters.clearCollectionSearchQuery}
          onSelectItem={pageFilters.toggleCollectionItem}
          onSelectClear={pageFilters.clearCollections}
          buttonPrefix="In"
          buttonLabel="Collections"
          selectedModeKey="in-any-of"
          onSelectMode={noop}
          noCollectionIsSelected={false}
          onNoCollectionIsSelected={noop}
          selectedItems={pageFilters.selectedCollectionTitles}
          supportModeSelection={false}
        />
      ),
    },
    {
      isActive: !!pageFilters.mediaKindsFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 1,
      component: (
        <MediaKindsFacetFilter
          key="media-kinds"
          items={pageFilters.mediaKindsFacetFilterItems}
          onSelectItem={pageFilters.toggleMediaKindItem}
          onSelectClear={pageFilters.clearMediaKinds}
          selectedItems={pageFilters.selectedMediaKinds}
        />
      ),
    },
    {
      isActive: !!pageFilters.dateParams,
      defaultOrder: 2,
      component: (
        <DateFacetFilter
          key="date"
          items={pageFilters.dateFilterItems}
          selectedRange={pageFilters.dateSelectedRange}
          onClear={() => pageFilters.clearDateFilter()}
          dateKindLabel={pageFilters.sortBy === SortByKind.LastCreated ? "Created" : "Edited"}
        />
      ),
    },
    {
      isActive: pageFilters.modifiedByFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 3,
      component: (
        <ContactsFacetFilter
          key="modifiedBy"
          items={pageFilters.modifiedByFacetFilterItems}
          searchText={pageFilters.modifiedBySearchQuery}
          onSearchTextChange={pageFilters.setModifiedBySearchQuery}
          onSearchTextClear={pageFilters.clearModifiedBySearchQuery}
          onSelectItem={pageFilters.toggleModifiedByItem}
          onSelectClear={pageFilters.clearModifiedBy}
          buttonPrefix="Edited by"
        />
      ),
    },
    {
      isActive: pageFilters.createdByFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 4,
      component: (
        <ContactsFacetFilter
          key="createdBy"
          items={pageFilters.createdByFacetFilterItems}
          searchText={pageFilters.createdBySearchQuery}
          onSearchTextChange={pageFilters.setCreatedBySearchQuery}
          onSearchTextClear={pageFilters.clearCreatedBySearchQuery}
          onSelectItem={pageFilters.toggleCreatedByItem}
          onSelectClear={pageFilters.clearCreatedBy}
          buttonPrefix="Created by"
        />
      ),
    },
  ];

  return filters
    .sort((a, b) => {
      const activeSort = Number(b.isActive) - Number(a.isActive);
      if (activeSort !== 0) {
        return activeSort;
      }

      return a.defaultOrder - b.defaultOrder;
    })
    .map(filter => filter.component);
};

const Wrapper = styled.div({
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
});

const StyledHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const FiltersWrapper = styled.div(({ theme }) => ({
  padding: theme.spacing["4"],
}));

const FiltersContainer = styled(MDSFadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing["2"],
}));

const PanelContainer = styled(MDSPanelContainer)`
  position: relative;
`;

const FloatingStatusPill = styled.div<{ $isError: boolean }>(({ theme, $isError }) => ({
  position: "absolute",
  bottom: theme.spacing["5"],
  left: "50%",
  zIndex: 2,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing["2"],
  maxWidth: "min(520px, calc(100% - 48px))",
  minHeight: 32,
  padding: `${theme.spacing["1"]} ${theme.spacing["3"]}`,
  color: $isError ? theme.colors.text.danger : theme.colors.text.onAccent,
  border: `1px solid ${
    $isError ? theme.colors.text.danger : theme.colors.background.button.emphasis.default
  }`,
  borderRadius: 999,
  background: $isError
    ? theme.colors.background.card.default
    : theme.colors.background.button.emphasis.default,
  boxShadow: "0 8px 24px rgb(0 0 0 / 16%)",
  transform: "translateX(-50%)",
}));

const StatusMessageText = styled(MDSText)<{ $isError: boolean }>`
  color: ${({ theme, $isError }) =>
    $isError ? theme.colors.text.danger : theme.colors.text.onAccent};
`;

const ExtraRowContent = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing["1"],
}));

const HintText = styled(MDSText)`
  display: block;
`;
